import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useStores } from "../../../../use-stores";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import { PartnerCustomerInventoryTag } from "../../../../../../models/inventory/PartnerCustomerInventoryTag";
import { InventoryVariantUpdateRequest } from "../../../../../../models/inventory/InventoryAdmin";

type EditableMetaItem = {
  clientSku: string;
  productName?: string | null;
  title?: string | null;
  packSize?: number | null;
  boxSize?: string | null;
  tagId?: string | null;
  tag?: string | null;
};

interface EditItemMetaPopupProps {
  open: boolean;
  item: EditableMetaItem | null;
  tags: PartnerCustomerInventoryTag[];
  onClose: () => void;
  onSubmit: (payload: InventoryVariantUpdateRequest) => void;
}

const EditItemMetaPopup: React.FC<EditItemMetaPopupProps> = observer(({
  open,
  item,
  tags,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const { userStore } = useStores();
  const canEditSku = userStore.hasPermission(EmployeeRoles.UPDATE_INVENTORY_SKU);
  const [clientSku, setClientSku] = useState("");
  const [packSize, setPackSize] = useState("");
  const [boxSize, setBoxSize] = useState("");
  const [tagId, setTagId] = useState("");
  const [tagLabel, setTagLabel] = useState("");

  const tagOptions: FilterValue[] = tags
    .filter((tag) => tag.active !== 0)
    .map((tag) => ({
      id: String(tag.tag_id),
      value: tag.tag,
    }));

  useEffect(() => {
    if (!open || !item) return;
    setClientSku(item.clientSku || "");
    setPackSize(
      item.packSize != null && Number.isFinite(item.packSize)
        ? String(item.packSize)
        : "",
    );
    setBoxSize(item.boxSize || "");
    setTagId(item.tagId || "");
    const matchedTag = tags.find(
      (tag) => String(tag.tag_id) === String(item.tagId || ""),
    );
    setTagLabel(matchedTag?.tag || item.tag || "");
  }, [open, item, tags]);

  const handleSubmit = () => {
    try {
      const trimmedSku = clientSku.trim();
      if (!trimmedSku) {
        return;
      }

      const packSizeRaw = packSize.trim();
      const parsedPackSize =
        packSizeRaw === "" ? undefined : Number(packSizeRaw);

      const payload: InventoryVariantUpdateRequest = {
        clientSku: trimmedSku,
      };
      if (
        parsedPackSize != null &&
        Number.isFinite(parsedPackSize) &&
        Number.isInteger(parsedPackSize) &&
        parsedPackSize >= 0
      ) {
        payload.packSize = parsedPackSize;
      }
      if (boxSize.trim()) {
        payload.boxSize = boxSize.trim();
      }
      if (tagId.trim()) {
        payload.tagId = tagId.trim();
      }

      onSubmit(payload);
    } catch (error) {
      console.error("Failed to submit item meta:", error);
    }
  };

  const packSizeRaw = packSize.trim();
  const hasInvalidPackSize =
    packSizeRaw !== "" &&
    (!Number.isFinite(Number(packSizeRaw)) ||
      !Number.isInteger(Number(packSizeRaw)) ||
      Number(packSizeRaw) < 0);
  const skuChanged = clientSku.trim() !== (item?.clientSku || "").trim();
  const canSubmit =
    clientSku.trim() !== "" &&
    !hasInvalidPackSize &&
    (skuChanged ||
      packSizeRaw !== "" ||
      boxSize.trim() !== "" ||
      tagId.trim() !== "");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{ zIndex: 2000 }}
    >
      <Box
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            <CustomTypography variant="heading4" fontWeight={700}>
              Edit Item Meta
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              {canEditSku
                ? "Update SKU, pack size, box size, and tag for this item"
                : "Update pack size, box size, and tag for this SKU"}
            </CustomTypography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  SKU
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    value={clientSku}
                    onChange={(e) => setClientSku(e.target.value)}
                    width="100%"
                    placeholder="Enter client SKU"
                    disabled={!canEditSku}
                  />
                </Box>
                {canEditSku && (
                  <CustomTypography
                    variant="body3"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Renames the client SKU. Catalog SKU, ledger, and shipment
                    lines are not rewritten.
                  </CustomTypography>
                )}
                <CustomTypography
                  variant="body3"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {item?.productName || item?.title || "—"}
                </CustomTypography>
              </Paper>
            </Grid2>

            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  PACK SIZE
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    type="number"
                    value={packSize}
                    onChange={(e) => setPackSize(e.target.value)}
                    width="100%"
                    placeholder="Enter pack size"
                    allowNegativeValue={false}
                    allowDecimalValue={false}
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  BOX SIZE
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    value={boxSize}
                    onChange={(e) => setBoxSize(e.target.value)}
                    width="100%"
                    placeholder="Enter box size"
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  TAG
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <AppSearchDropdownMulti
                    isMultiSelect={false}
                    title="Select tag"
                    local
                    list={tagOptions}
                    value={
                      tagId
                        ? [{ id: tagId, value: tagLabel || tagId }]
                        : []
                    }
                    handleSelect={(selected) => {
                      if (!selected.length) {
                        setTagId("");
                        setTagLabel("");
                        return;
                      }
                      setTagId(String(selected[0].id));
                      setTagLabel(selected[0].value);
                    }}
                    fontSize="12px"
                    minWidth="100%"
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2
              size={12}
              display="flex"
              justifyContent="flex-end"
              gap={1.5}
              sx={{ pt: 1 }}
            >
              <AppButton label="Cancel" onClick={onClose} variant="grayscale" />
              <AppButton
                label="Save"
                onClick={handleSubmit}
                variant="primary"
                disabled={!canSubmit}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
      </Box>
    </Dialog>
  );
});

export default EditItemMetaPopup;
